/**
 * @file Select Account Identity component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import SelectAccountIdentityContainer from '../../containers/discover/SelectAccountIdentityContainer';
import CreateAccountIdentityContainer, {
  CreateAccountIdentityComponentContainer,
} from '../../apps/consumer/containers/CreateAccountIdentityContainer';
import UpdateAccountIdentityContainer from '../../apps/consumer/containers/UpdateAccountIdentityContainer';
import {
  CREATE_ACCOUNT_IDENTITY_MODAL,
  DISCOVER_IDENTITY_SELECTED_ALBUMS,
} from '../../constants/modals';
import ContextualString from '../contextual/ContextualString';
import Spinner from '../global/Spinner';
import {
  ALBUM_IDENTITY_ACCESS,
  IDENTITY_RELATIONSHIP,
} from '../../constants/identity';
import {
  getAccountIdentityNamesArray,
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../util/identity';
import DiscoverAlbumFolderAlbumSelectionModalContainer from '../../containers/discover/DiscoverAlbumFolderAlbumSelectionModalContainer';
import {
  albumFolderSubscriptionRequired,
  requiresGenderForOnboarding,
} from '../../util/album-folder';
import { sentryCaptureMessage } from '../../util/sentry';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import OnboardingLayoutScrollableContent from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';
import AngleRightIcon from '../icons/AngleRightIcon';
import { getAsset } from '../../util/assets';
import CustomControl from '../global/CustomControl';
import Button from '../global/Button';

class SelectAccountIdentities extends Component {
  static propTypes = {
    accountId: PropTypes.string,
    identities: PropTypes.arrayOf(PropTypes.object),
    onSelectIdentity: PropTypes.func.isRequired,
    onPurchaseIdentity: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    loadingIdentities: PropTypes.bool,
    loadingAlbumFolder: PropTypes.bool,
    albumFolder: PropTypes.shape({
      album_type: PropTypes.string,
      album_subtype: PropTypes.string,
      account: PropTypes.shape({
        name: PropTypes.string,
        organization_name: PropTypes.string,
      }),
      album_identity_access: PropTypes.oneOf(
        Object.values(ALBUM_IDENTITY_ACCESS),
      ),
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }),
    useAlbumSelectionModal: PropTypes.bool,
  };

  static defaultProps = {
    identities: [],
  };

  state = {
    showSelectionForIdentity: null,
    identityFilterStepCompleted: false,
    identityFilter: null,
  };

  // eslint-disable-next-line
  loggedError = false;

  componentDidUpdate() {
    const { albumFolder, accountId } = this.props;

    if (
      albumFolder &&
      albumFolder.album_identity_access === ALBUM_IDENTITY_ACCESS.NONE &&
      !this.loggedError
    ) {
      // When the response is ALBUM_IDENTITY_ACCESS.NONE normally
      // we'd want to block identity selection and display a message to the
      // user who for some reason does not have access to this gallery,
      // but since this does not fall under any of our business cases
      // right now we need to allow access (even broken as it will
      // display all identities) and log this for further investigation
      // as to why it happened.
      sentryCaptureMessage(
        'Select Account Identities :: ALBUM_IDENTITY_ACCESS.NONE',
        {
          level: 'warning',
          extra: {
            accountId,
          },
        },
      );

      this.loggedError = true;
    }
  }

  onAddFace = event => {
    event.preventDefault();
    const { openModal, albumFolder, history } = this.props;

    if (albumFolderSubscriptionRequired(albumFolder.root)) {
      history.push({
        pathname: `/purchase/album-folder/${albumFolder.root.id}/purchase-subscription`,
        state: {
          fromDiscovery: true,
        },
      });

      return;
    }

    openModal(CREATE_ACCOUNT_IDENTITY_MODAL);
  };

  onShowSelection = identity => {
    const { openModal } = this.props;

    this.setState(
      () => ({
        showSelectionForIdentity: identity,
      }),
      () => {
        openModal(DISCOVER_IDENTITY_SELECTED_ALBUMS);
      },
    );
  };

  onIdentityUpdatedOrCreated = identity => {
    const { onSelectIdentity } = this.props;

    onSelectIdentity(identity);
  };

  onIdentityFilterChange = filter => {
    this.setState({ identityFilter: filter });
  };

  renderIdentity(identity) {
    const {
      useAlbumSelectionModal,
      onPurchaseIdentity,
      albumFolder,
      onSelectIdentity,
    } = this.props;

    const { identityFilter } = this.state;

    const hasPurchasedSubscription = get(
      identity,
      'purchased_album_folders.records',
      [],
    ).some(
      purchasedFolder => purchasedFolder.id === get(albumFolder, 'root.id'),
    );

    return (
      <SelectAccountIdentityContainer
        key={identity.id}
        identity={identity}
        identityFilter={identityFilter}
        onSelectIdentity={onSelectIdentity}
        onShowSelection={
          useAlbumSelectionModal ? this.onShowSelection : undefined
        }
        requiresPurchase={
          albumFolderSubscriptionRequired(albumFolder.root) &&
          !hasPurchasedSubscription
        }
        onPurchase={onPurchaseIdentity}
        requiresGender={requiresGenderForOnboarding(albumFolder.root)}
      />
    );
  }

  render() {
    const {
      identities,
      albumFolder,
      loadingIdentities,
      loadingAlbumFolder,
      useAlbumSelectionModal,
      accountId,
    } = this.props;

    const {
      showSelectionForIdentity,
      identityFilterStepCompleted,
      identityFilter,
    } = this.state;

    const sortedIdentities = sortByIdentityRelationship(identities ?? []);
    const hasOnlyMeIdentity =
      identities.length === 1 && identities[0]?.name === 'Me';

    // Check if we should show the filter step
    const showFilterStep =
      !loadingIdentities &&
      !loadingAlbumFolder &&
      albumFolder &&
      !identityFilterStepCompleted &&
      hasOnlyMeIdentity &&
      albumFolder.album_identity_access === ALBUM_IDENTITY_ACCESS.ALL &&
      sortedIdentities[0].relationship === IDENTITY_RELATIONSHIP.PRIMARY;

    const filteredIdentities = (() => {
      if (!albumFolder) {
        return [];
      }

      if (!identityFilter) {
        return identitiesFilteredOnAccess(
          sortedIdentities,
          albumFolder.album_identity_access,
        );
      }

      return sortedIdentities.filter(
        identity => identity.relationship === identityFilter,
      );
    })();

    if (loadingAlbumFolder || loadingIdentities) {
      return (
        <div className="text-center">
          <Spinner />
        </div>
      );
    }

    return (
      <Fragment>
        <WaldoSpeechBubble position="right" className="mb-4">
          Tell me who you want me to look for!
        </WaldoSpeechBubble>

        {(loadingIdentities || loadingAlbumFolder) && (
          <Spinner className="d-block mx-auto my-2" />
        )}

        {showFilterStep && (
          <div className="w-100 mb-4">
            <h4 className="mb-3">I&apos;m looking for...</h4>
            <div className="identity-filter-custom-control">
              <CustomControl
                type="checkbox"
                shape="round"
                id="secondary-filter"
                label={
                  <span>
                    My{' '}
                    <ContextualString
                      id="identityReference"
                      type={albumFolder?.album_type}
                      subtype={albumFolder?.album_subtype}
                      formatter={identityRef =>
                        identityRef === 'member' ? 'family member' : identityRef
                      }
                    />
                  </span>
                }
                checked={identityFilter === IDENTITY_RELATIONSHIP.SECONDARY}
                onChange={() =>
                  this.onIdentityFilterChange(IDENTITY_RELATIONSHIP.SECONDARY)
                }
              />
            </div>
            <div className="identity-filter-custom-control">
              <CustomControl
                type="checkbox"
                shape="round"
                id="primary-filter"
                label="Myself"
                checked={identityFilter === IDENTITY_RELATIONSHIP.PRIMARY}
                onChange={() =>
                  this.onIdentityFilterChange(IDENTITY_RELATIONSHIP.PRIMARY)
                }
              />
            </div>
            <Button
              btnBlock
              className="mt-2"
              disabled={!identityFilter}
              onClick={() =>
                this.setState(() => ({ identityFilterStepCompleted: true }))
              }
            >
              Continue
            </Button>
          </div>
        )}

        {!showFilterStep && (
          <Fragment>
            {!loadingIdentities &&
            !loadingAlbumFolder &&
            filteredIdentities.length === 0 ? (
              <div className="w-100">
                <CreateAccountIdentityComponentContainer
                  includeTitle={false}
                  onIdentityCreated={this.onIdentityUpdatedOrCreated}
                  stringContext={albumFolder}
                  identityNames={getAccountIdentityNamesArray(sortedIdentities)}
                  noCancelOption
                />
              </div>
            ) : (
              <Fragment>
                <OnboardingLayoutScrollableContent className="w-100">
                  <div className="identity-list-container">
                    {filteredIdentities.map(identity =>
                      this.renderIdentity(identity),
                    )}

                    {identityFilter !== IDENTITY_RELATIONSHIP.PRIMARY &&
                      albumFolder?.album_identity_access !==
                        ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY &&
                      filteredIdentities.length > 0 && (
                        <button
                          className="new-identity-container"
                          onClick={this.onAddFace}
                        >
                          <img
                            src={getAsset('/icon-add-face-blue.svg')}
                            alt=""
                            className="mr-2"
                            width="52"
                            height="52"
                          />
                          <div className="mb-0 d-flex justify-content-between align-items-center">
                            <div className="text-content">
                              <ContextualString
                                id="consumerAddNewIdentity"
                                defaultMessage="Find photos of a family member"
                                type={albumFolder && albumFolder.album_type}
                                subtype={
                                  albumFolder && albumFolder.album_subtype
                                }
                              />
                            </div>
                            <div>
                              <AngleRightIcon
                                color="#1983fb"
                                height="18px"
                                className="ml-1 mt-1"
                              />
                            </div>
                          </div>
                        </button>
                      )}
                  </div>
                </OnboardingLayoutScrollableContent>
              </Fragment>
            )}
          </Fragment>
        )}

        <CreateAccountIdentityContainer
          onIdentityCreated={this.onIdentityUpdatedOrCreated}
          stringContext={albumFolder}
          identityNames={getAccountIdentityNamesArray(sortedIdentities)}
          requiresSelfieChecklistVerification
        />
        <UpdateAccountIdentityContainer
          stringContext={albumFolder}
          requiresSelfieChecklistVerification
          onIdentityUpdated={this.onIdentityUpdatedOrCreated}
        />
        {showSelectionForIdentity && useAlbumSelectionModal && (
          <DiscoverAlbumFolderAlbumSelectionModalContainer
            selectedIdentity={showSelectionForIdentity}
            albumFolder={albumFolder}
            includeIdentityNavModal
            accountId={accountId}
          />
        )}
      </Fragment>
    );
  }
}

export default SelectAccountIdentities;
